import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { mmsJobPause, mmsJobResume, mmsPauseReasons } from '../../../../services/mmsApi';
import { locationService } from '../../../../services/locationService';
import { getParts } from '../../../../models/mmsModel';
import { useAuth } from '../../../../context/AuthContext';
import { getDisplayNameFromObject, getMmsMobileWorkerFromStorage, getMmsActorName } from '../../../../utils/mmsUser';

// Available tools for bulk water repairs
const AVAILABLE_TOOLS = [
  { id: 'tlb', name: 'TLB (Tractor Loader Backhoe)', icon: '🚜' },
  { id: 'excavator', name: 'Excavator', icon: '🏗️' },
  { id: 'compressor', name: 'Air Compressor', icon: '🔧' },
  { id: 'generator', name: 'Generator', icon: '📦' },
  { id: 'pump', name: 'Water Pump', icon: '💧' },
  { id: 'welder', name: 'Welding Equipment', icon: '🔥' },
  { id: 'cutter', name: 'Pipe Cutter', icon: '✂️' },
  { id: 'wrench', name: 'Large Wrench Set', icon: '🔩' },
  { id: 'human_resource', name: 'Human Resource', icon: '👥' }
];

// Workflow Steps
const WORKFLOW_STEPS = [
  { 
    id: 'assessment', 
    label: 'Assessment', 
    buttonLabel: 'Complete Assessment',
    title: 'Assessment',
    description: 'Select tools, estimate completion, and document the problem',
    icon: '🔍'
  },
  { 
    id: 'parts', 
    label: 'Parts', 
    buttonLabel: 'Complete Parts List',
    title: 'Identify Required Parts',
    description: 'List all parts needed to complete this job',
    icon: '🔧'
  },
  { 
    id: 'procurement', 
    label: 'Procurement', 
    buttonLabel: 'Complete Procurement',
    title: 'Procure Parts',
    description: 'Track procurement from store or suppliers',
    icon: '📦'
  },
  { 
    id: 'work', 
    label: 'Work', 
    buttonLabel: 'Complete Work',
    title: 'Complete the Work',
    description: 'Perform the repair and document with photos',
    icon: '⚙️'
  },
  { 
    id: 'verification', 
    label: 'Verification', 
    buttonLabel: 'Complete Verification',
    title: 'Verify & Close Job',
    description: 'Final verification and job closure',
    icon: '✓'
  }
];

const MMSMobileJobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [activities, setActivities] = useState([]);
  
  // Job start tracking
  const [jobStarted, setJobStarted] = useState(false);
  const [startLocation, setStartLocation] = useState(null);
  
  // Workflow states
  const [currentStep, setCurrentStep] = useState(0);
  const [workflowData, setWorkflowData] = useState({
    assessment: { 
      description: '', 
      photos: [], 
      tools: [], // Array of selected tools
      estimatedCompletion: '', // Estimated hours/days
      waterStatus: 'on', // 'on', 'off', 'restored'
      waterShutOffTime: null,
      waterRestoredTime: null,
      completed: false,
      skipped: false
    },
    parts: { items: [], completed: false, skipped: false },
    procurement: { notes: '', completed: false, skipped: false },
    work: { notes: '', photos: [], completed: false, skipped: false },
    verification: { notes: '', waterRestored: false, completed: false, skipped: false }
  });
  
  // UI states
  const [, setShowAddPart] = useState(false);
  const [newPart, setNewPart] = useState({ description: '', quantity: 1, source: '' });
  const fileInputRef = useRef(null);
  
  // Pause reason state
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseReasonId, setPauseReasonId] = useState('');
  const [pauseReasons, setPauseReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  
  // Resume with image state
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeNotes, setResumeNotes] = useState('');
  const [resumeImage, setResumeImage] = useState(null);
  const [resumeImagePreview, setResumeImagePreview] = useState(null);
  const resumeImageInputRef = useRef(null);
  
  // Child jobs state
  const [childJobs, setChildJobs] = useState([]);
  const [loadingChildJobs, setLoadingChildJobs] = useState(false);
  const [creatingChildJob, setCreatingChildJob] = useState(false);
  
  // Parts autocomplete states
  const [partsSuggestions, setPartsSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingParts, setSearchingParts] = useState(false);
  const [isSelectingPart, setIsSelectingPart] = useState(false);
  const partsInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Load available pause reasons
  const loadPauseReasons = useCallback(async () => {
    try {
      setLoadingReasons(true);
      const response = await mmsPauseReasons();
      console.log('Pause reasons API response:', response);
      if (response.data.status === 1 && response.data.data && response.data.data.reasons) {
        console.log('Setting pause reasons:', response.data.data.reasons);
        setPauseReasons(response.data.data.reasons);
      } else {
        console.warn('Unexpected response structure');
      }
    } catch (error) {
      console.error('Error loading pause reasons:', error);
    } finally {
      setLoadingReasons(false);
    }
  }, []);

  // Debounced search for parts autocomplete
  const searchParts = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setPartsSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setSearchingParts(true);
      const response = await getParts(searchTerm, '', 20);
      if (response && response.data && response.data.parts) {
        setPartsSuggestions(response.data.parts);
        setShowSuggestions(true);
      } else {
        setPartsSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error searching parts:', error);
      setPartsSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setSearchingParts(false);
    }
  }, []);

  // Debounce search with useEffect
  useEffect(() => {
    // Skip search if user just selected a part (prevents re-search after selection)
    if (isSelectingPart) {
      // Don't reset flag here - let the button handler reset it after delay
      return;
    }
    
    const timer = setTimeout(() => {
      if (newPart.description && newPart.description.length >= 2) {
        // User is typing, perform search
        searchParts(newPart.description);
      } else {
        // Clear suggestions if input is too short or empty
        setPartsSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [newPart.description, searchParts, isSelectingPart]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        partsInputRef.current &&
        !partsInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle job start
  const handleStartJob = async () => {
    try {
      setUpdating(true);
      
      // Get current location
      const position = await locationService.getCurrentLocation();
      
      if (!position) {
        toast.error('Unable to get your location. Please enable GPS.');
        return;
      }

      setStartLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timestamp: new Date().toISOString()
      });

      // Log job start activity
      const userName = getMmsActorName(user);
      await api.post('/api/mms/assignments/add-activity', {
        assignment_id: jobId,
        activity_type: 'job_started',
        notes: `Job started by ${userName}`,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });

      // Update job status to in-progress if not already
      if (job?.assignment_status !== 'in-progress') {
        await api.post('/api/mms/assignments/update-status', {
          assignment_id: jobId,
          status: 'in-progress',
          notes: `Job started by ${userName}`,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      }

      setJobStarted(true);
      fetchActivities();
      toast.success('Job started! Location recorded.');
    } catch (error) {
      console.error('Error starting job:', error);
      toast.error('Failed to start job');
    } finally {
      setUpdating(false);
    }
  };

  // Fetch child jobs
  const fetchChildJobs = useCallback(async () => {
    if (!jobId) return;
    
    try {
      setLoadingChildJobs(true);
      const response = await api.get(`/api/mms/assignments/${jobId}/children`);
      if (response.data?.status === 1 && response.data?.data?.child_jobs) {
        setChildJobs(response.data.data.child_jobs);
      }
    } catch (error) {
      console.error('Error fetching child jobs:', error);
      // Silently fail - child jobs are optional
    } finally {
      setLoadingChildJobs(false);
    }
  }, [jobId]);

  // Workflow handlers
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Create photo objects with preview URLs
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    // Determine which step we're on (assessment or work)
    const step = currentStep === 0 ? 'assessment' : 'work';
    
    setWorkflowData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        photos: [...(prev[step]?.photos || []), ...newPhotos]
      }
    }));
    
    toast.success(`${files.length} photo(s) added`);
  };

  const handleAddPart = () => {
    if (!newPart.description.trim()) {
      toast.warning('Please enter part description');
      return;
    }

    // If NONE was selected, add it and immediately close
    if (newPart.description === 'NONE - No parts required') {
      setWorkflowData(prev => ({
        ...prev,
        parts: {
          ...prev.parts,
          items: [{ description: 'NONE - No parts required', quantity: 1, source: 'none', id: Date.now() }]
        }
      }));
      setNewPart({ description: '', quantity: 1, source: '' });
      setShowAddPart(false);
      toast.success('Marked as no parts required');
      return;
    }

    setWorkflowData(prev => ({
      ...prev,
      parts: {
        ...prev.parts,
        items: [...prev.parts.items, { ...newPart, id: Date.now() }]
      }
    }));

    setNewPart({ description: '', quantity: 1, source: '' });
    setShowAddPart(false);
    toast.success('Part added to list');
  };

  const handleRemovePart = (partId) => {
    setWorkflowData(prev => ({
      ...prev,
      parts: {
        ...prev.parts,
        items: prev.parts.items.filter(p => p.id !== partId)
      }
    }));
  };

  const handlePauseJob = async (reasonId, notes) => {
    if (!jobStarted) {
      toast.warning('Job must be started before pausing');
      return;
    }

    if (!reasonId) {
      toast.warning('Please select a pause reason');
      return;
    }

    try {
      setUpdating(true);
      
      // Call new pause endpoint
      await mmsJobPause(jobId, reasonId, notes);

      // Close modal and reset
      setShowPauseModal(false);
      setPauseReasonId('');
      setPauseReason('');
      
      // Refresh job data
      await fetchJobDetails();
      toast.success('Job paused. You can resume it from the Paused Jobs list.');
    } catch (error) {
      console.error('Error pausing job:', error);
      toast.error('Failed to pause job: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenPauseModal = () => {
    setShowPauseModal(true);
  };

  const handleConfirmPause = () => {
    handlePauseJob(pauseReasonId, pauseReason);
  };

  const handleResumeJob = async () => {
    if (!jobId) {
      toast.warning('Job ID not found');
      return;
    }

    try {
      setUpdating(true);
      
      // Call resume endpoint with optional image
      await mmsJobResume(jobId, resumeNotes, resumeImage);

      // Close modal and reset
      setShowResumeModal(false);
      setResumeNotes('');
      setResumeImage(null);
      setResumeImagePreview(null);
      
      // Refresh job data
      await fetchJobDetails();
      toast.success('Job resumed successfully!');
    } catch (error) {
      console.error('Error resuming job:', error);
      toast.error('Failed to resume job: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setUpdating(false);
    }
  };

  const handleResumeImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setResumeImage(file);
    setResumeImagePreview(URL.createObjectURL(file));
  };

  const handleSkipStep = async (stepIndex) => {
    const step = WORKFLOW_STEPS[stepIndex];
    
    // Ensure job is started
    if (!jobStarted) {
      toast.warning('Please start the job first');
      return;
    }

    // Check if job is paused
    if (job?.assignment_status === 'paused' || job?.assignment_status === 'on-hold') {
      toast.warning('Please resume the job before skipping steps');
      return;
    }

    // Step 1 (Parts): Can always be skipped
    // Step 2 (Procurement): Can only be skipped if no parts needed
    if (stepIndex === 1) {
      // Parts step - allow skip
    } else if (stepIndex === 2) {
      // Procurement step - only allow skip if no parts or only NONE
      const hasActualParts = workflowData.parts.items.some(p => p.description !== 'NONE - No parts required');
      if (hasActualParts) {
        toast.warning('Cannot skip Procurement step when parts need to be collected or purchased');
        return;
      }
    } else {
      // Other steps cannot be skipped
      toast.warning('Only Parts and Procurement steps can be skipped');
      return;
    }

    try {
      setUpdating(true);
      
      // Mark step as skipped immediately
      setWorkflowData(prev => ({
        ...prev,
        [step.id]: { ...prev[step.id], skipped: true, completed: true }
      }));

      // Move to next step immediately (before async operations)
      const nextStep = stepIndex < WORKFLOW_STEPS.length - 1 ? stepIndex + 1 : stepIndex;
      setCurrentStep(nextStep);

      // Log activity
      const position = await locationService.getCurrentLocation();
      
      await api.post('/api/mms/assignments/add-activity', {
        assignment_id: jobId,
        activity_type: 'workflow_step',
        notes: `Skipped: ${step.label} - Not required for this job`,
        latitude: position?.coords.latitude,
        longitude: position?.coords.longitude
      });

      // Refresh activities but don't let it reset our step navigation
      // We'll update workflow data but preserve current step if we've moved ahead
      const currentStepBeforeRefresh = nextStep;
      await fetchActivities();
      
      // Ensure we stay on the step we navigated to
      if (currentStep !== currentStepBeforeRefresh) {
        setCurrentStep(currentStepBeforeRefresh);
      }
      
      toast.success(`${step.label} step skipped`);
    } catch (error) {
      console.error('Error skipping step:', error);
      toast.error('Failed to skip step: ' + (error.message || 'Unknown error'));
      // Revert step change on error
      setCurrentStep(stepIndex);
    } finally {
      setUpdating(false);
    }
  };

  const handleStepClick = (stepIndex) => {
    // Allow clicking on any step to navigate to it
    // But only if job is started
    if (!jobStarted) {
      toast.warning('Please start the job first');
      return;
    }

    // Check if job is paused
    if (job?.assignment_status === 'paused' || job?.assignment_status === 'on-hold') {
      toast.warning('Please resume the job before navigating to steps');
      return;
    }

    setCurrentStep(stepIndex);
  };

  const handleSaveDescription = async () => {
    if (!workflowData.assessment.description.trim()) {
      toast.warning('Please enter a description before saving');
      return;
    }

    if (!jobStarted) {
      toast.warning('Please start the job first');
      return;
    }

    try {
      setUpdating(true);
      
      // Get current location
      const position = await locationService.getCurrentLocation();
      
      // Save description as a draft activity
      await api.post('/api/mms/assignments/add-activity', {
        assignment_id: jobId,
        activity_type: 'assessment_draft',
        notes: `Assessment description saved (draft):\n\n${workflowData.assessment.description}`,
        latitude: position?.coords?.latitude,
        longitude: position?.coords?.longitude
      });

      // Refresh activities to show the new activity immediately
      await fetchActivities();
      
      toast.success('Description saved successfully');
    } catch (error) {
      console.error('Error saving description:', error);
      toast.error('Failed to save description: ' + (error.message || 'Unknown error'));
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteStep = async (stepIndex) => {
    const step = WORKFLOW_STEPS[stepIndex];
    
    // Ensure job is started
    if (!jobStarted) {
      toast.warning('Please start the job first');
      return;
    }

    // Check if job is paused
    if (job?.assignment_status === 'paused' || job?.assignment_status === 'on-hold') {
      toast.warning('Please resume the job before completing steps');
      return;
    }
    
    // Validate step completion
    if (stepIndex === 0 && !workflowData.assessment.description.trim()) {
      toast.warning('Please describe the issue before proceeding');
      return;
    }

    // Parts and Procurement steps can be skipped, so don't validate them
    if (stepIndex === 3 && !workflowData.work.notes.trim()) {
      toast.warning('Please add work completion notes');
      return;
    }

    try {
      setUpdating(true);
      
      // Check if tools are selected in assessment step - create child jobs
      if (stepIndex === 0 && workflowData.assessment.tools.length > 0) {
        setCreatingChildJob(true);
        for (const toolId of workflowData.assessment.tools) {
          try {
            const response = await api.post(`/api/mms/assignments/${jobId}/create-child-job`, {
              tool_type: toolId
            });
            
            if (response.data?.status === 1) {
              const childJobNo = response.data?.data?.child_jobcard_no || 'Unknown';
              toast.success(`Child job ${childJobNo} created for ${toolId}`);
            }
          } catch (childError) {
            console.error(`Error creating child job for ${toolId}:`, childError);
            toast.warning(`Failed to create child job for ${toolId}`);
          }
        }
        setCreatingChildJob(false);
        
        // Refresh child jobs list
        await fetchChildJobs();
      }
      
      // Mark step as completed
      setWorkflowData(prev => ({
        ...prev,
        [step.id]: { ...prev[step.id], completed: true }
      }));

      // Log activity
      const position = await locationService.getCurrentLocation();
      
      // Build activity notes with step-specific data
      let activityNotes = `Completed: ${step.label}`;
      
      // For Assessment step, include tools if selected
      if (step.id === 'assessment' && workflowData.assessment.tools.length > 0) {
        const toolNames = workflowData.assessment.tools.map(toolId => {
          const tool = AVAILABLE_TOOLS.find(t => t.id === toolId);
          return tool ? tool.name : toolId;
        }).join(', ');
        activityNotes += ` - Tools Required: ${toolNames}`;
      }
      
      // For Parts step, include parts list in notes
      if (step.id === 'parts' && workflowData.parts.items.length > 0) {
        const partsList = workflowData.parts.items.map(p => 
          `${p.description} (Qty: ${p.quantity}, Source: ${p.source})`
        ).join('; ');
        activityNotes += ` - Parts: ${partsList}`;
      } else if (step.id === 'parts' && workflowData.parts.items.length === 0) {
        activityNotes += ' - No parts required';
      }
      
      // For Procurement step, include notes if any
      if (step.id === 'procurement' && workflowData.procurement.notes) {
        activityNotes += ` - Notes: ${workflowData.procurement.notes}`;
      }
      
      // For Work step, include notes
      if (step.id === 'work' && workflowData.work.notes) {
        activityNotes += ` - Work done: ${workflowData.work.notes}`;
      }
      
      const activityResponse = await api.post('/api/mms/assignments/add-activity', {
        assignment_id: jobId,
        activity_type: 'workflow_step',
        notes: activityNotes,
        latitude: position?.coords.latitude,
        longitude: position?.coords.longitude
      });

      // Upload photos if any for this step
      const stepPhotos = workflowData[step.id]?.photos || [];
      
      if (stepPhotos.length > 0 && activityResponse.data?.data?.activity_id) {
        const formData = new FormData();
        stepPhotos.forEach((photoObj) => {
          formData.append('files', photoObj.file);
        });

        try {
          // Get worker token from localStorage for authentication
          const workerToken = localStorage.getItem('mms_mobile_token');
          
          await api.post(
            `/api/mms/activities/${activityResponse.data.data.activity_id}/attachments`, 
            formData,
            {
              headers: {
                'Authorization': `Bearer ${workerToken}`
              }
            }
          );
          toast.success(`${stepPhotos.length} photo(s) uploaded successfully`);
        } catch (uploadError) {
          console.error('Photo upload error:', uploadError);
          toast.warning('Photos uploaded with errors');
        }
      }
      
      // Move to next step
      if (stepIndex < WORKFLOW_STEPS.length - 1) {
        setCurrentStep(stepIndex + 1);
      }
      
      fetchActivities();
      toast.success(`${step.label} completed!`);
    } catch (error) {
      console.error('Error completing step:', error);
      toast.error('Failed to complete step');
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteJob = async () => {
    // Ensure first 4 steps are completed (verification is the final step being completed now)
    const requiredSteps = WORKFLOW_STEPS.slice(0, 4); // All except verification
    const allPreviousCompleted = requiredSteps.every(step => workflowData[step.id]?.completed);

    if (!allPreviousCompleted) {
      toast.warning('Please complete all previous workflow steps first');
      return;
    }

    try {
      setUpdating(true);

      // Get current gps location for completion
      const position = await locationService.getCurrentLocation();

      // Build notes from current workflowData (capture before updating state)
      const notesString = `Job completed via workflow.\n\nIssue: ${workflowData.assessment.description}\n\nWork Done: ${workflowData.work.notes}\n\nVerification: ${workflowData.verification.notes || 'Verified and completed'}`;

      // Mark verification as completed locally
      setWorkflowData(prev => ({
        ...prev,
        verification: { ...prev.verification, completed: true }
      }));

      const completionData = {
        assignment_id: jobId,
        status: 'completed',
        notes: notesString,
        latitude: position?.coords.latitude,
        longitude: position?.coords.longitude
      };

      const response = await api.post('/api/mms/assignments/update-status', completionData);

      if (response.data && (response.data.success || response.data.status === 1)) {
        toast.success('Job completed successfully!');

        // Update local job state so UI reflects completion immediately
        setJob(prevJob => ({
          ...prevJob,
          assignment_status: 'completed',
          status: 'completed',
          completed_at: new Date().toISOString(),
          completion_latitude: position?.coords.latitude,
          completion_longitude: position?.coords.longitude
        }));

        // Refresh activities to show completion activity
        try {
          await fetchActivities();
        } catch (innerErr) {
          console.warn('Warning refreshing activities after completion:', innerErr);
        }

        // Navigate back to jobs list after short delay
        setTimeout(() => navigate('/mms-mobile/jobs'), 1500);
      } else {
        // API returned failure
        console.error('Completion API response:', response.data);
        toast.error(response.data?.message || 'Failed to complete job');
      }
    } catch (error) {
      console.error('Error completing job:', error);
      toast.error(error.response?.data?.message || 'Failed to complete job');
    } finally {
      setUpdating(false);
    }
  };

  const fetchJobDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/mms/mobile/jobs`);
      
      console.log('Job Details - Full Response:', response.data);
      console.log('Looking for job ID:', jobId, 'Type:', typeof jobId);
      
      // Handle both success formats: {success: true} and {status: 1}
      const isSuccess = response.data.success === true || response.data.status === 1;
      
      if (isSuccess && response.data.data) {
        const jobs = response.data.data.jobs || [];
        console.log('Available jobs:', jobs);
        console.log('Job IDs:', jobs.map(j => ({ id: j.assignment_id, type: typeof j.assignment_id })));
        
        const foundJob = jobs.find(
          j => j.assignment_id === parseInt(jobId)
        );
        
        console.log('Found job:', foundJob);
        
        if (foundJob) {
          setJob(foundJob);
        } else {
          console.error('Job not found. JobId:', jobId, 'Available IDs:', jobs.map(j => j.assignment_id));
          toast.error('Job not found');
          navigate('/mms-mobile/jobs');
        }
      } else {
        console.error('Invalid response structure:', response.data);
        toast.error('Failed to load jobs');
        navigate('/mms-mobile/jobs');
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId, navigate]);

  const fetchActivities = useCallback(async () => {
    try {
      const status = String(job?.assignment_status || '').toLowerCase();
      const jobcardNo = job?.jobcard_no || job?.JOBCARD_NO || null;

      // When job is completed, show full activity history related to the jobcard
      const url = (status === 'completed' && jobcardNo)
        ? `/api/mms/activities/${jobcardNo}`
        : `/api/mms/assignments/${jobId}/activities`;

      const response = await api.get(url);
      
      // Handle both success formats: {success: true} and {status: 1}
      const isSuccess = response.data.success === true || response.data.status === 1;
      if (isSuccess) {
        const payload = response.data.data || response.data;
        const activities = payload.activities || [];
        setActivities(activities);
        
        // Check if job was already started
        const jobStartActivity = activities.find(a => a.activity_type === 'job_started');
        
        if (jobStartActivity) {
          setJobStarted(true);
          if (jobStartActivity.latitude && jobStartActivity.longitude) {
            setStartLocation({
              latitude: jobStartActivity.latitude,
              longitude: jobStartActivity.longitude,
              timestamp: jobStartActivity.created_at
            });
          }
        }
        
        // Restore workflow progress from completed step activities
        const completedSteps = activities.filter(a => 
          a.activity_type === 'workflow_step' && 
          (a.description?.startsWith('Completed: ') || a.description?.startsWith('Skipped: '))
        );
        
        if (completedSteps.length > 0) {
          let lastCompletedStepIndex = -1;
          
          WORKFLOW_STEPS.forEach((step, index) => {
            const stepCompleted = completedSteps.some(activity => 
              activity.description?.startsWith(`Completed: ${step.label}`) ||
              activity.description?.startsWith(`Skipped: ${step.label}`)
            );
            
            if (stepCompleted) {
              lastCompletedStepIndex = index;
            }
          });

          // Map completed steps to workflow data
          setWorkflowData(prevWorkflowData => {
            const newWorkflowData = { ...prevWorkflowData };
            WORKFLOW_STEPS.forEach((step) => {
              const stepCompleted = completedSteps.some(activity =>
                activity.description?.startsWith(`Completed: ${step.label}`) ||
                activity.description?.startsWith(`Skipped: ${step.label}`)
              );

              const stepSkipped = completedSteps.some(activity =>
                activity.description?.startsWith(`Skipped: ${step.label}`)
              );

              if (stepCompleted) {
                newWorkflowData[step.id] = {
                  ...newWorkflowData[step.id],
                  completed: true,
                  skipped: stepSkipped
                };
              }
            });
            return newWorkflowData;
          });
          
          // Only auto-navigate to next step if user hasn't manually navigated ahead
          // Don't reset step if user is already on a later step
          const nextUncompletedStep = lastCompletedStepIndex >= 0 && lastCompletedStepIndex < WORKFLOW_STEPS.length - 1 
            ? lastCompletedStepIndex + 1 
            : -1;
          
          // Only update step if:
          // 1. We found a next step to go to, AND
          // 2. Current step is not already ahead of where we want to go
          setCurrentStep(prevStep => {
            if (nextUncompletedStep >= 0 && prevStep <= lastCompletedStepIndex) {
              return nextUncompletedStep;
            }
            if (lastCompletedStepIndex === WORKFLOW_STEPS.length - 1 && prevStep < WORKFLOW_STEPS.length - 1) {
              return WORKFLOW_STEPS.length - 1;
            }
            return prevStep;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, [job?.assignment_status, job?.jobcard_no, job?.JOBCARD_NO, jobId]);

  useEffect(() => {
    fetchJobDetails();
    fetchActivities();
    loadPauseReasons();
    fetchChildJobs();
  }, [fetchJobDetails, fetchActivities, loadPauseReasons, fetchChildJobs]);

  // If a job is completed, load full history by jobcard (includes assignment-linked activity)
  useEffect(() => {
    const status = String(job?.assignment_status || '').toLowerCase();
    const jobcardNo = job?.jobcard_no || job?.JOBCARD_NO || null;
    if (status === 'completed' && jobcardNo) {
      fetchActivities();
    }
  }, [fetchActivities, job?.assignment_status, job?.jobcard_no, job?.JOBCARD_NO]);

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'in-progress': 'bg-blue-50 text-blue-700 border-blue-200',
      'completed': 'bg-green-50 text-green-700 border-green-200',
      'on-hold': 'bg-orange-50 text-orange-700 border-orange-200',
      'cancelled': 'bg-red-50 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityActorName = (activity) => {
    // Check created_by_name first (portal user who created the activity)
    const createdBy = activity?.created_by_name;
    if (createdBy && String(createdBy).trim() && !/unknown user/i.test(createdBy)) {
      return String(createdBy).trim();
    }

    // Check worker_name (field worker)
    const workerName = activity?.worker_name;
    if (workerName && String(workerName).trim() && !/unknown user/i.test(workerName)) {
      return String(workerName).trim();
    }

    const desc = String(activity?.description || activity?.notes || '').trim();

    // Try extract a name from common activity messages
    const mJobStarted = desc.match(/\bJob\s+started\s+by\s+([^\n\r]+)$/i);
    if (mJobStarted && mJobStarted[1] && !/unknown user/i.test(mJobStarted[1])) {
      return mJobStarted[1].trim();
    }
    const mBy = desc.match(/\bby\s+([^\n\r]+)\s*$/i);
    if (mBy && mBy[1] && !/unknown user/i.test(mBy[1])) {
      return mBy[1].trim();
    }

    // If this activity belongs to the logged-in worker, use their stored name
    const storedWorker = getMmsMobileWorkerFromStorage();
    const storedWorkerId = storedWorker?.worker_id != null ? String(storedWorker.worker_id) : null;
    const activityWorkerId = activity?.worker_id != null ? String(activity.worker_id) : null;
    if (storedWorkerId && activityWorkerId && storedWorkerId === activityWorkerId) {
      return getDisplayNameFromObject(storedWorker);
    }

    return null;
  };

  // Function to replace placeholders (artisan/Unknown User) with best-known actor name
  const formatActivityNotes = (notes, activity) => {
    if (!notes) return notes;
    const actorName = getActivityActorName(activity) || getMmsActorName(user);

    let text = String(notes);

    if (actorName && !/unknown user/i.test(actorName)) {
      text = text.replace(/\bartisan\b/gi, actorName);
      text = text.replace(/\bJob\s+started\s+by\s+Unknown User\b/gi, `Job started by ${actorName}`);
      text = text.replace(/\bby\s+Unknown User\b/gi, `by ${actorName}`);
    }

    return text;
  };

  // Function to replace 'Unknown User' in assignment notes with known worker name
  const formatAssignmentNotes = (notes) => {
    if (!notes) return notes;
    const workerName = job?.worker_name || getMmsMobileWorkerFromStorage()?.worker_name || getMmsActorName(user);
    if (workerName && !/unknown user/i.test(workerName)) {
      return notes
        .replace(/\bJob\s+started\s+by\s+Unknown User\b/gi, `Job started by ${workerName}`)
        .replace(/\bby\s+Unknown User\b/gi, `by ${workerName}`);
    }
    return notes;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Job not found</p>
          <button
            onClick={() => navigate('/mms-mobile/jobs')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F7FA' }}>
      {/* Header - Matching Style */}
      <div 
        className="text-white px-4 py-4 sticky top-0 z-20" 
        style={{ 
          background: 'linear-gradient(135deg, #0099E0 0%, #0086D4 100%)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate('/mms-mobile/jobs')}
            className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>Job Details</h1>
            <p className="text-sm text-white opacity-90">{job.jobcard_no || `Job #${job.assignment_id}`}</p>
          </div>
          {/* Pause Button in Header */}
          {jobStarted && (job?.assignment_status !== 'completed') && (job?.assignment_status !== 'paused') && (job?.assignment_status !== 'on-hold') && (
            <button
              onClick={handleOpenPauseModal}
              disabled={updating}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Pause Job"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4 pb-20">
        {/* Start Job Section */}
        {!jobStarted ? (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Ready to Start?</h3>
              <p className="text-gray-600 mb-6">
                Click below to start this job. Your location will be recorded and tracked throughout the workflow.
              </p>
              <button
                onClick={handleStartJob}
                disabled={updating}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: updating ? '#9CA3AF' : 'linear-gradient(135deg, #0099E0 0%, #0086D4 100%)' }}
              >
                {updating ? 'Starting Job...' : '▶ Start Job'}
              </button>
              {startLocation && (
                <p className="mt-3 text-xs text-gray-500">
                  Location: {startLocation.latitude.toFixed(6)}, {startLocation.longitude.toFixed(6)}
                </p>
              )}
            </div>
          </div>
        ) : (job?.assignment_status === 'paused' || job?.assignment_status === 'on-hold') ? (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <span className="text-4xl">⏸️</span>
              </div>
              <h3 className="text-xl font-semibold text-orange-800 mb-2">Job Paused</h3>
              <p className="text-orange-600 mb-6">
                This job is currently paused. You can resume it when ready to continue.
              </p>
              <button
                onClick={handleResumeJob}
                disabled={updating}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <span>▶</span>
                <span>{updating ? 'Resuming...' : 'Resume Job'}</span>
              </button>
            </div>
          </div>
        ) : job?.assignment_status === 'completed' ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">Job Completed</p>
                <p className="text-xs text-green-700">
                  {job.completed_at ? `Completed: ${formatDateTime(job.completed_at)}` : 'All workflow steps completed'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">Job In Progress</p>
                <p className="text-xs text-blue-700">
                  Location recorded: {startLocation?.latitude.toFixed(4)}, {startLocation?.longitude.toFixed(4)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Original Job Description - Show prominently - Filter out workflow notes */}
        {(() => {
          // ONLY use JOB_DESCRIPTION from EDAMS - no fallbacks
          const description = job?.JOB_DESCRIPTION;
          // Don't show if it's the workflow completion text or empty
          if (!description || description.includes('Job completed via workflow')) {
            return null;
          }
          return (
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-semibold text-blue-900">📋 Job Description</h3>
                {jobStarted && (job.property_latitude && job.property_longitude) && (
                  <button
                    onClick={() => {
                      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${job.property_latitude},${job.property_longitude}&travelmode=driving`;
                      window.open(googleMapsUrl, '_blank');
                    }}
                    className="p-2 hover:opacity-80 transition-opacity flex-shrink-0"
                    title="Navigate to location"
                  >
                    <svg width="32px" height="32px" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                      <path d="M63.3 512.2a448.5 448 0 1 0 897 0 448.5 448 0 1 0-897 0Z" fill="#00c400"></path>
                      <path d="M416.09375 605.09375c1.21875 0.5625 2.15625 1.5 2.71875 2.71875l82.3125 175.6875c3.1875 6.84375 12.84375 7.03125 15.75 0.375l201.84375-465.75c3.5625-8.15625-4.78125-16.5-12.9375-12.9375L240.125 507.03125c-6.75 2.90625-6.5625 12.5625 0.375 15.75l175.59375 82.3125z" fill="#ffffff"></path>
                    </svg>
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-line mb-3">
                {description}
              </p>
              {job.property_address && (
                <div className="text-xs text-gray-600 pt-2 border-t border-blue-200">
                  <p className="font-medium">📍 {job.property_address}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Child Jobs Section - Show if parent is blocked or has child jobs */}
        {(job?.blocking_status || childJobs.length > 0) && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-yellow-900 mb-3 flex items-center gap-2">
              {job?.blocking_status ? '🚧' : '🔗'} 
              {job?.blocking_status ? 'Job Blocked - Equipment Required' : 'Related Jobs'}
            </h3>
            
            {job?.blocking_status && (
              <div className="mb-3 p-3 bg-yellow-100 rounded border border-yellow-200">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ This job cannot proceed until required equipment is delivered
                </p>
              </div>
            )}
            
            {creatingChildJob && (
              <div className="mb-3 p-3 bg-blue-50 rounded border border-blue-200 text-sm text-blue-700">
                Creating child jobs for equipment delivery...
              </div>
            )}
            
            {loadingChildJobs ? (
              <div className="text-sm text-gray-600">Loading child jobs...</div>
            ) : childJobs.length > 0 ? (
              <div className="space-y-2">
                {childJobs.map(child => (
                  <div key={child.relationship_id} className="p-3 bg-white rounded border border-yellow-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">
                          Job #{child.child_jobcard_no}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {child.blocking_reason || `${child.required_tool} delivery`}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        child.relationship_status === 'resolved' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {child.relationship_status === 'resolved' ? '✓ Delivered' : '⏳ Pending'}
                      </span>
                    </div>
                    {child.child_assigned_to && (
                      <p className="text-xs text-gray-500">
                        Assigned to: {child.child_assigned_to}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-600">No child jobs yet</p>
            )}
          </div>
        )}

        {/* Workflow Progress */}
        {jobStarted && job?.assignment_status !== 'completed' && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Job Progress</h2>
          
          {/* Progress Steps */}
          <div className="flex justify-between mb-6 relative">
            {WORKFLOW_STEPS.map((step, index) => {
              const isCompleted = workflowData[step.id]?.completed;
              const isSkipped = workflowData[step.id]?.skipped;
              const isCurrent = currentStep === index;
              const isClickable = jobStarted && (job?.assignment_status !== 'paused' && job?.assignment_status !== 'on-hold');
              
              return (
                <div key={step.id} className="flex flex-col items-center flex-1 relative">
                  <button
                    type="button"
                    onClick={() => isClickable && handleStepClick(index)}
                    disabled={!isClickable}
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold mb-2 transition-all ${
                      isCompleted || isSkipped
                        ? 'bg-green-500 text-white'
                        : isCurrent
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    } ${
                      isClickable ? 'cursor-pointer hover:scale-110 hover:shadow-md' : 'cursor-default'
                    }`}
                    title={isSkipped ? `${step.label} (Skipped)` : isCompleted ? `${step.label} (Completed)` : `Go to ${step.label}`}
                  >
                    {isSkipped ? '⊘' : isCompleted ? '✓' : index + 1}
                  </button>
                  <span className={`text-xs text-center max-w-[60px] ${
                    isCurrent ? 'text-blue-600 font-medium' : isCompleted || isSkipped ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {step.label}
                  </span>
                  {index < WORKFLOW_STEPS.length - 1 && (
                    <div className={`absolute w-full h-0.5 top-5 left-1/2 -z-10 ${
                      (workflowData[WORKFLOW_STEPS[index + 1].id]?.completed || workflowData[WORKFLOW_STEPS[index + 1].id]?.skipped) ? 'bg-green-500' : 'bg-gray-200'
                    }`} style={{ width: 'calc(100% / 5)' }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Current Step Content */}
          {currentStep < WORKFLOW_STEPS.length && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-semibold text-gray-700 mb-3">{WORKFLOW_STEPS[currentStep].title}</h3>
              <p className="text-sm text-gray-600 mb-4">{WORKFLOW_STEPS[currentStep].description}</p>

              {/* Step 1: Assessment */}
              {currentStep === 0 && (
                <div className="space-y-4">
                  {/* Pause/Resume Job Section */}
                  {jobStarted && (
                    <div className={`rounded-lg p-4 border-2 ${
                      job?.assignment_status === 'paused' || job?.assignment_status === 'on-hold'
                        ? 'bg-orange-50 border-orange-300'
                        : 'bg-blue-50 border-blue-300'
                    }`}>
                      {job?.assignment_status === 'paused' || job?.assignment_status === 'on-hold' ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-orange-800">
                            <span className="text-2xl">⏸️</span>
                            <div>
                              <div className="font-semibold">Job Paused</div>
                              {job?.assignment_notes && job.assignment_notes.includes('Job paused') ? (
                                <div className="text-sm text-orange-600">
                                  {formatAssignmentNotes(job.assignment_notes.replace('Job paused - ', ''))}
                                </div>
                              ) : (
                                <div className="text-sm text-orange-600">Job is currently paused</div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={handleResumeJob}
                            disabled={updating}
                            className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <span>▶</span>
                            <span>{updating ? 'Resuming...' : 'Resume Job'}</span>
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-blue-800">
                            <span className="text-2xl">⚙️</span>
                            <div>
                              <div className="font-semibold">Job In Progress</div>
                              <div className="text-sm text-blue-600">Working on assessment</div>
                            </div>
                          </div>
                          <button
                            onClick={handleOpenPauseModal}
                            disabled={updating}
                            className="w-full px-4 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <span>⏸</span>
                            <span>{updating ? 'Pausing...' : 'Pause Job'}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tools Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tools Required</label>
                    <div className="grid grid-cols-2 gap-2">
                      {AVAILABLE_TOOLS.map(tool => (
                        <button
                          key={tool.id}
                          type="button"
                          onClick={() => {
                            const tools = workflowData.assessment.tools.includes(tool.id)
                              ? workflowData.assessment.tools.filter(t => t !== tool.id)
                              : [...workflowData.assessment.tools, tool.id];
                            setWorkflowData({
                              ...workflowData,
                              assessment: { ...workflowData.assessment, tools }
                            });
                          }}
                          className={`p-3 border-2 rounded-lg text-left transition-all ${
                            workflowData.assessment.tools.includes(tool.id)
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'
                          }`}
                        >
                          <div className="text-2xl mb-1">{tool.icon}</div>
                          <div className="text-xs font-medium">{tool.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Estimated Completion Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Completion Time</label>
                    <select
                      value={workflowData.assessment.estimatedCompletion}
                      onChange={(e) => setWorkflowData({
                        ...workflowData,
                        assessment: { ...workflowData.assessment, estimatedCompletion: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select estimated time...</option>
                      <option value="1-2 hours">1-2 hours</option>
                      <option value="3-4 hours">3-4 hours</option>
                      <option value="5-8 hours">5-8 hours (1 day)</option>
                      <option value="2 days">2 days</option>
                      <option value="3 days">3 days</option>
                      <option value="1 week">1 week</option>
                      <option value="2 weeks">2 weeks</option>
                      <option value="1 month">1 month</option>
                    </select>
                  </div>

                  {/* Water Shut-Off Control */}
                  <div className="border-2 border-orange-200 bg-orange-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-orange-900 mb-3">Water Supply Control</label>
                    <div className="space-y-3">
                      {/* Current Status Section */}
                      <div className="bg-white rounded-lg p-3 border border-orange-200">
                        <div className="font-medium text-gray-900 mb-2">Current Status</div>
                        <div className={`flex items-center gap-2 text-sm ${
                          workflowData.assessment.waterStatus === 'off' ? 'text-red-600' : 
                          workflowData.assessment.waterStatus === 'restored' ? 'text-green-600' : 
                          'text-blue-600'
                        }`}>
                          <span className="text-xl">
                            {workflowData.assessment.waterStatus === 'off' ? '🔴' : 
                             workflowData.assessment.waterStatus === 'restored' ? '✅' : 
                             '💧'}
                          </span>
                          <span>
                            {workflowData.assessment.waterStatus === 'off' ? 'Water Shut Off' : 
                             workflowData.assessment.waterStatus === 'restored' ? 'Water Restored' : 
                             'Water Running'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Action Button Section */}
                      <button
                        type="button"
                        onClick={() => {
                          const newStatus = workflowData.assessment.waterStatus === 'on' ? 'off' : 
                                          workflowData.assessment.waterStatus === 'off' ? 'restored' : 'on';
                          const updates = {
                            waterStatus: newStatus,
                            waterShutOffTime: newStatus === 'off' ? new Date().toISOString() : workflowData.assessment.waterShutOffTime,
                            waterRestoredTime: newStatus === 'restored' ? new Date().toISOString() : workflowData.assessment.waterRestoredTime
                          };
                          setWorkflowData({
                            ...workflowData,
                            assessment: { ...workflowData.assessment, ...updates }
                          });
                          toast.success(newStatus === 'off' ? 'Water shut off recorded' : 
                                      newStatus === 'restored' ? 'Water restoration recorded' : 'Status updated');
                        }}
                        className={`w-full px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                          workflowData.assessment.waterStatus === 'on' ? 'bg-red-500 hover:bg-red-600 text-white' :
                          workflowData.assessment.waterStatus === 'off' ? 'bg-green-500 hover:bg-green-600 text-white' :
                          'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                      >
                        <span className="text-lg">
                          {workflowData.assessment.waterStatus === 'on' ? '🔴' :
                           workflowData.assessment.waterStatus === 'off' ? '✅' :
                           '🔄'}
                        </span>
                        <span>
                          {workflowData.assessment.waterStatus === 'on' ? 'Shut Off Water/Electricity' :
                           workflowData.assessment.waterStatus === 'off' ? 'Restore Water/Electricity' :
                           'Reset Status'}
                        </span>
                      </button>
                      
                      {workflowData.assessment.waterShutOffTime && (
                        <div className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                          <strong>Shut off:</strong> {new Date(workflowData.assessment.waterShutOffTime).toLocaleString()}
                        </div>
                      )}
                      {workflowData.assessment.waterRestoredTime && (
                        <div className="text-xs text-green-700 bg-green-50 p-2 rounded border border-green-200">
                          <strong>Restored:</strong> {new Date(workflowData.assessment.waterRestoredTime).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Issue Description</label>
                      <button
                        type="button"
                        onClick={handleSaveDescription}
                        disabled={updating || !workflowData.assessment.description.trim()}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        {updating ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                    <textarea
                      value={workflowData.assessment.description}
                      onChange={(e) => setWorkflowData({
                        ...workflowData,
                        assessment: { ...workflowData.assessment, description: e.target.value }
                      })}
                      rows={4}
                      placeholder="Describe the issue in detail..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    {workflowData.assessment.description.trim() && (
                      <p className="mt-1 text-xs text-gray-500">
                        {workflowData.assessment.description.length} characters
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors"
                    >
                      + Add Photos
                    </button>
                    
                    {workflowData.assessment.photos.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {workflowData.assessment.photos.map((photo, index) => (
                          <div key={index} className="relative">
                            <img src={photo.preview} alt="Issue" className="w-full h-20 object-cover rounded-lg" />
                            <button
                              onClick={() => {
                                const newPhotos = workflowData.assessment.photos.filter((_, i) => i !== index);
                                setWorkflowData({
                                  ...workflowData,
                                  assessment: { ...workflowData.assessment, photos: newPhotos }
                                });
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Identify Parts */}
              {currentStep === 1 && (
                <div className="space-y-3">
                  <div className="flex gap-2 relative">
                    <div className="flex-1 relative">
                      <input
                        ref={partsInputRef}
                        type="text"
                        value={newPart.description || ''}
                        onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                        disabled={false}
                        onFocus={() => {
                          // Show suggestions if we have them and user has typed enough
                          if (partsSuggestions.length > 0 && !isSelectingPart && newPart.description && newPart.description.length >= 2) {
                            setShowSuggestions(true);
                          } else if (newPart.description && newPart.description.length >= 2) {
                            // If user focuses and has typed enough, trigger search
                            searchParts(newPart.description);
                          }
                        }}
                        onBlur={(e) => {
                          // Delay closing to allow button click to register
                          setTimeout(() => {
                            // Only close if we're not selecting (click happened)
                            if (!isSelectingPart) {
                              setShowSuggestions(false);
                              // Clear suggestions if input loses focus and we're not selecting
                              if (!e.relatedTarget || !e.relatedTarget.closest('[data-suggestions]')) {
                                setPartsSuggestions([]);
                              }
                            }
                          }, 200);
                        }}
                        placeholder="Start typing part name..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {searchingParts && (
                        <div className="absolute right-3 top-2.5">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      
                      {/* Autocomplete Suggestions */}
                      {showSuggestions && (partsSuggestions.length > 0 || newPart.description.length < 2) && (
                        <div
                          ref={suggestionsRef}
                          data-suggestions="true"
                          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                        >
                          {/* NONE Option */}
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsSelectingPart(true);
                              setNewPart({
                                ...newPart,
                                description: 'NONE - No parts required'
                              });
                              setTimeout(() => {
                                setShowSuggestions(false);
                                setPartsSuggestions([]);
                              }, 0);
                              setTimeout(() => {
                                setIsSelectingPart(false);
                              }, 500);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-amber-50 transition-colors border-b border-gray-100 cursor-pointer font-medium text-amber-700"
                          >
                            <div>NONE - No parts required</div>
                            <div className="text-xs text-amber-600 mt-0.5">Skip to next step without adding parts</div>
                          </button>
                          
                          {partsSuggestions.map((part) => (
                            <button
                              key={part.part_id}
                              type="button"
                              onMouseDown={(e) => {
                                // Use onMouseDown to prevent input blur and re-search
                                e.preventDefault();
                                e.stopPropagation();
                                
                                // Set selecting flag first to prevent any re-searches
                                setIsSelectingPart(true);
                                
                                // Set the part name
                                setNewPart({
                                  ...newPart,
                                  description: part.part_name
                                });
                                
                                // Immediately close dropdown and clear suggestions
                                // Use setTimeout(0) to ensure state updates happen after React processes the click
                                setTimeout(() => {
                                  setShowSuggestions(false);
                                  setPartsSuggestions([]);
                                }, 0);
                                
                                // Reset flag after delay to allow any pending effects to skip
                                setTimeout(() => {
                                  setIsSelectingPart(false);
                                }, 500);
                              }}
                              onClick={(e) => {
                                // Backup handler to ensure dropdown closes
                                e.preventDefault();
                                e.stopPropagation();
                                setIsSelectingPart(true);
                                setShowSuggestions(false);
                                setPartsSuggestions([]);
                                setTimeout(() => {
                                  setIsSelectingPart(false);
                                }, 500);
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer"
                            >
                              <div className="font-medium text-gray-900">{part.part_name}</div>
                              {part.category && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {part.category} {part.unit && `• ${part.unit}`}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="number"
                      value={newPart.quantity || 1}
                      onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                      placeholder="Qty"
                      min="1"
                      disabled={false}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <select
                    value={newPart.source || ''}
                    onChange={(e) => setNewPart({ ...newPart, source: e.target.value })}
                    disabled={false}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Source</option>
                    <option value="stock">Stock</option>
                    <option value="stores">Stores</option>
                  </select>

                  <button
                    onClick={handleAddPart}
                    disabled={!newPart.description || (newPart.description !== 'NONE - No parts required' && (!newPart.quantity || !newPart.source))}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    + Add Part
                  </button>

                  {workflowData.parts.items.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h4 className="font-medium text-gray-700">Parts List</h4>
                      {workflowData.parts.items.map((part, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{part.description}</p>
                            <p className="text-sm text-gray-600">
                              Qty: {part.quantity} | Source: {part.source === 'stock' ? 'Stock (Vehicle)' : part.source === 'stores' ? 'Stores' : part.source}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRemovePart(index)}
                            className="ml-2 w-8 h-8 bg-red-500 text-white rounded-full text-sm"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Procurement */}
              {currentStep === 2 && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Parts Required:</strong> {workflowData.parts.items.length} item(s)
                    </p>
                    {workflowData.parts.items.some(p => p.source === 'stores') && (
                      <p className="text-sm text-blue-700 mt-2">
                        Items from Stores need to be collected before work can begin.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    {workflowData.parts.items.map((part, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{part.description}</p>
                          <p className="text-sm text-gray-600">
                            Qty: {part.quantity} | {part.source === 'stock' ? 'Stock (Vehicle)' : part.source === 'stores' ? 'Stores' : part.source}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          part.procured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {part.procured ? 'Ready' : 'Pending'}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Procurement Notes</label>
                    <textarea
                      value={workflowData.procurement.notes}
                      onChange={(e) => setWorkflowData({
                        ...workflowData,
                        procurement: { ...workflowData.procurement, notes: e.target.value }
                      })}
                      rows={3}
                      placeholder="Add notes about parts procurement..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Complete Work */}
              {currentStep === 3 && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Completed Notes</label>
                    <textarea
                      value={workflowData.work.notes}
                      onChange={(e) => setWorkflowData({
                        ...workflowData,
                        work: { ...workflowData.work, notes: e.target.value }
                      })}
                      rows={4}
                      placeholder="Describe the work performed..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Completion Photos</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const newPhotos = files.map(file => ({
                          file,
                          preview: URL.createObjectURL(file)
                        }));
                        setWorkflowData({
                          ...workflowData,
                          work: {
                            ...workflowData.work,
                            photos: [...workflowData.work.photos, ...newPhotos]
                          }
                        });
                      }}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors"
                    >
                      + Add Completion Photos
                    </button>

                    {workflowData.work.photos.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {workflowData.work.photos.map((photo, index) => (
                          <div key={index} className="relative">
                            <img src={photo.preview} alt="Work" className="w-full h-20 object-cover rounded-lg" />
                            <button
                              onClick={() => {
                                const newPhotos = workflowData.work.photos.filter((_, i) => i !== index);
                                setWorkflowData({
                                  ...workflowData,
                                  work: { ...workflowData.work, photos: newPhotos }
                                });
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 5: Verification */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">Job Summary</h4>
                    <div className="space-y-2 text-sm text-green-800">
                      <p><strong>Issue:</strong> {workflowData.assessment.description}</p>
                      <p><strong>Tools Used:</strong> {workflowData.assessment.tools.map(id => 
                        AVAILABLE_TOOLS.find(t => t.id === id)?.name || id
                      ).join(', ') || 'None'}</p>
                      <p><strong>Estimated Time:</strong> {workflowData.assessment.estimatedCompletion || 'Not specified'}</p>
                      <p><strong>Parts Used:</strong> {workflowData.parts.items.length} item(s)</p>
                      <p><strong>Work Notes:</strong> {workflowData.work.notes}</p>
                      <p><strong>Photos:</strong> {workflowData.assessment.photos.length + workflowData.work.photos.length} uploaded</p>
                    </div>
                  </div>

                  {/* Water Restoration Confirmation */}
                  <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-blue-900 mb-3">Water Supply Status</label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-blue-200">
                        <input
                          type="checkbox"
                          id="waterRestored"
                          checked={workflowData.verification.waterRestored}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setWorkflowData({
                              ...workflowData,
                              verification: { 
                                ...workflowData.verification, 
                                waterRestored: isChecked 
                              },
                              assessment: {
                                ...workflowData.assessment,
                                waterStatus: isChecked ? 'restored' : workflowData.assessment.waterStatus,
                                waterRestoredTime: isChecked ? new Date().toISOString() : workflowData.assessment.waterRestoredTime
                              }
                            });
                          }}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <label htmlFor="waterRestored" className="flex-1 text-sm font-medium text-gray-900 cursor-pointer">
                          ✅ Confirm that water supply has been restored
                        </label>
                      </div>
                      
                      {workflowData.assessment.waterShutOffTime && (
                        <div className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                          <div><strong>Shut off:</strong> {new Date(workflowData.assessment.waterShutOffTime).toLocaleString()}</div>
                          {workflowData.assessment.waterRestoredTime && (
                            <div className="text-green-700 mt-1">
                              <strong>Restored:</strong> {new Date(workflowData.assessment.waterRestoredTime).toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Final Verification Notes</label>
                    <textarea
                      value={workflowData.verification.notes}
                      onChange={(e) => setWorkflowData({
                        ...workflowData,
                        verification: { ...workflowData.verification, notes: e.target.value }
                      })}
                      rows={3}
                      placeholder="Add any final notes or observations..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <button
                    onClick={handleCompleteJob}
                    disabled={updating}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updating ? 'Completing Job...' : '✓ Complete Job'}
                  </button>
                </div>
              )}

              {/* Step Navigation */}
              {currentStep < 4 && (
                <div className="flex gap-2 mt-4">
                  <div className="flex gap-2">
                    {currentStep > 0 && (
                      <button
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                      >
                        ← Previous
                      </button>
                    )}
                    
                    {/* Show Skip button for Parts and Procurement steps */}
                    {(currentStep === 1 || currentStep === 2) && !workflowData[WORKFLOW_STEPS[currentStep].id]?.skipped && (
                      <button
                        onClick={() => handleSkipStep(currentStep)}
                        disabled={updating || (currentStep === 2 && workflowData.parts.items.some(p => p.description !== 'NONE - No parts required'))}
                        title={currentStep === 2 && workflowData.parts.items.some(p => p.description !== 'NONE - No parts required') ? 'Cannot skip when parts need to be procured' : ''}
                        className="px-4 py-2 border border-orange-300 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Skip
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleCompleteStep(currentStep)}
                      disabled={updating}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updating ? 'Processing...' : `${WORKFLOW_STEPS[currentStep].buttonLabel} →`}
                    </button>
                  </div>
                  
                  {/* Show message if step is skipped */}
                  {workflowData[WORKFLOW_STEPS[currentStep].id]?.skipped && (
                    <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-800">
                        <strong>This step was skipped.</strong> You can still add parts or procurement information if needed.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Job Information */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-700">Job Information</h2>
            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(job.assignment_status)}`}>
              {job.assignment_status}
            </span>
          </div>
          
          <div className="space-y-3 text-sm">
            {/* Job Description is already shown prominently at the top, so we don't duplicate it here */}

            {job.customer_name && (
              <div>
                <span className="text-gray-500 block mb-1">Customer</span>
                <span className="text-gray-900 font-medium">{job.customer_name}</span>
              </div>
            )}
            
            {job.property_address && (
              <div>
                <span className="text-gray-500 block mb-1">Address</span>
                <span className="text-gray-900">{job.property_address}</span>
              </div>
            )}
            
            {job.application_id && (
              <div>
                <span className="text-gray-500 block mb-1">Application ID</span>
                <span className="text-gray-900 font-medium">#{job.application_id}</span>
              </div>
            )}
            
            {job.assigned_at && (
              <div>
                <span className="text-gray-500 block mb-1">Assigned Date</span>
                <span className="text-gray-900">{formatDateTime(job.assigned_at)}</span>
              </div>
            )}
            
            {job.assignment_notes && (
              <div>
                <span className="text-gray-500 block mb-1">Assignment Notes</span>
                <span className="text-gray-900 italic">{formatAssignmentNotes(job.assignment_notes)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Activity History */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-3">Activity History</h2>
          
          {activities.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No activities recorded yet</p>
          ) : (
            <div className="space-y-3">
              {activities.map((activity, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-3 py-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 capitalize">{activity.activity_type.replace('_', ' ')}</p>
                      {activity.description && (
                        <p className="text-sm text-gray-600 mt-1">{formatActivityNotes(activity.description, activity)}</p>
                      )}
                      
                      {/* Display attachments if any */}
                      {activity.attachments && activity.attachments.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {activity.attachments.map((attachment, idx) => {
                            const uploadBaseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                              ? 'http://silulumanzi.host' 
                              : '';
                            const imageUrl = `${uploadBaseUrl}/uploads/mms/activities/${attachment.file_path}`;
                            
                            return (
                              <div key={idx} className="relative">
                                {attachment.is_image ? (
                                  <img 
                                    src={imageUrl}
                                    alt={attachment.file_name}
                                    className="w-full h-24 object-cover rounded border border-gray-200 cursor-pointer"
                                    onClick={() => window.open(imageUrl, '_blank')}
                                  />
                                ) : (
                                  <a 
                                    href={imageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center w-full h-24 bg-gray-100 rounded border border-gray-200 text-xs text-gray-600 hover:bg-gray-200"
                                  >
                                    📎 {attachment.file_name}
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatDateTime(activity.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pause Job Modal */}
      {showPauseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pause Job</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a reason for pausing this job:
            </p>
            
            {/* Pause Reason Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={pauseReasonId}
                onChange={(e) => setPauseReasonId(e.target.value)}
                disabled={loadingReasons || updating}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select a reason...</option>
                {pauseReasons.map(reason => (
                  <option key={reason.reason_id} value={reason.reason_id}>
                    {reason.reason_name}
                  </option>
                ))}
              </select>
              {loadingReasons && (
                <p className="text-xs text-gray-500 mt-1">Loading reasons...</p>
              )}
            </div>

            {/* Additional Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes (optional)
              </label>
              <textarea
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="Add any additional details..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                disabled={updating}
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPauseModal(false);
                  setPauseReasonId('');
                  setPauseReason('');
                }}
                disabled={updating}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPause}
                disabled={updating || !pauseReasonId}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? 'Pausing...' : 'Pause Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Job Modal */}
      {showResumeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resume Job</h3>
            <p className="text-sm text-gray-600 mb-4">
              Ready to continue work? You can attach an image if needed (e.g., stock collected):
            </p>

            {/* Resume Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resume Notes (optional)
              </label>
              <textarea
                value={resumeNotes}
                onChange={(e) => setResumeNotes(e.target.value)}
                placeholder="Any notes about resuming work..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={updating}
              />
            </div>

            {/* Image Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attach Image (optional)
              </label>
              {resumeImagePreview ? (
                <div className="relative mb-2">
                  <img 
                    src={resumeImagePreview} 
                    alt="Preview" 
                    className="w-full h-40 object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    onClick={() => {
                      setResumeImage(null);
                      setResumeImagePreview(null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    disabled={updating}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => resumeImageInputRef.current?.click()}
                  disabled={updating}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-center hover:border-blue-500 transition-colors disabled:opacity-50"
                >
                  <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <p className="text-sm text-gray-600">Click to upload image</p>
                </button>
              )}
              <input
                ref={resumeImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleResumeImageChange}
                className="hidden"
                disabled={updating}
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowResumeModal(false);
                  setResumeNotes('');
                  setResumeImage(null);
                  setResumeImagePreview(null);
                }}
                disabled={updating}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleResumeJob}
                disabled={updating}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? 'Resuming...' : 'Resume Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MMSMobileJobDetails;
